import React from 'react';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@core-ui/Components/ui/table';
import { cn } from '@core-ui-lib/utils';
import { EmptyState } from '@ui/shared/emptyState';
import { useTranslation } from 'react-i18next';
import { Pencil, Trash2 } from 'lucide-react';

import type { VoicemailRow } from '@features/voicemail/pages/VoicemailsDashboard';
import VoicemailTableSkeleton from '@features/voicemail/components/VoicemailTableSkeleton.tsx';

interface Props {
  loading: boolean;
  rows: VoicemailRow[];
  onPlay: (vm: VoicemailRow) => void | Promise<void>;
  isPlaying: (id: string) => boolean;
  progress: number;

  fmtDuration: (sec: number) => string;
  fmtDate: (iso: string) => string;

  WaveMini: React.ComponentType<{ progress?: number; animated?: boolean }>;
  PlayIcon: React.ComponentType<{ playing: boolean }>;

  onEdit: (vm: VoicemailRow) => void | Promise<void>;
  onAskDelete: (vm: VoicemailRow) => void | Promise<void>;

  skeletonRows?: number;
}

export default function VoicemailTable ({
  loading,
  rows,
  onPlay,
  isPlaying,
  progress,
  fmtDuration,
  fmtDate,
  WaveMini,
  PlayIcon,
  onEdit,
  onAskDelete,
  skeletonRows = 10,
}: Props): React.JSX.Element {
  const { t } = useTranslation();

  rows = rows.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

  return (
    <div className="w-full overflow-auto rounded-md border border-border bg-card">
      <Table>
        <TableHeader className="bg-muted/60">
          <TableRow className="border-b border-border/60">
            <TableHead className="w-[56px]" />
            <TableHead>{t('voice.voicemails.table.name', { defaultValue: 'Name' })}</TableHead>
            <TableHead>{t('voice.voicemails.table.waveform', { defaultValue: 'Waveform' })}</TableHead>
            <TableHead>{t('voice.voicemails.table.duration', { defaultValue: 'Duration' })}</TableHead>
            <TableHead className="text-center">
              {t('voice.voicemails.table.actions', { defaultValue: 'Actions' })}
            </TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {loading ? (
            <VoicemailTableSkeleton rows={skeletonRows} />
          ) : rows.length === 0 ? (
            <tr data-qa-id="vm-not-found">
              <td colSpan={6} className="py-10">
                <EmptyState message={t('voice.voicemails.empty', { defaultValue: 'No voicemails found.' })} />
              </td>
            </tr>
          ) : (
            rows.map((vm, index) => {
              const playing = isPlaying(vm.id);

              return (
                <TableRow
                  key={vm.id}
                  className={cn('border-b border-border hover:bg-muted/30')}
                  data-qa-id={`vm-row-${index}`}
                >
                  <TableCell className="py-3">
                    <button
                      type="button"
                      onClick={() => {
                        void onPlay(vm);
                      }}
                      className={cn(
                        'flex h-9 w-9 items-center justify-center rounded-full border transition',
                        playing ? 'bg-sky-100 text-sky-700' : 'bg-background text-sky-700 hover:bg-muted/60',
                      )}
                      aria-label={playing ? 'Pause' : 'Play'}
                      title={playing ? 'Pause' : 'Play'}
                    >
                      <PlayIcon playing={playing} />
                    </button>
                  </TableCell>

                  <TableCell className="py-3 min-w-[240px]">
                    <div className="truncate font-medium text-foreground" data-qa-id="vm-name">{vm.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {t('voice.voicemails.updated', { defaultValue: 'Updated' })} {fmtDate(vm.updatedAt)}
                    </div>
                  </TableCell>

                  <TableCell className="py-3">
                    <WaveMini progress={playing ? progress : 0} animated={playing} />
                  </TableCell>

                  <TableCell className="py-3 text-sm text-foreground">
                    {vm.audioUrl !== '' && vm.durationSec === 0 ? (
                      <div className="h-4 w-10 rounded bg-muted animate-pulse" />
                    ) : (
                      fmtDuration(vm.durationSec)
                    )}
                  </TableCell>

                  <TableCell className="py-3 text-center">
                    <div className="flex justify-center gap-3">
                      <button
                        type="button"
                        data-qa-id="edit-vm"
                        onClick={() => {
                          void onEdit(vm);
                        }}
                        className="cursor-pointer"
                        aria-label={t('common.edit', { defaultValue: 'Edit' })}
                        title={t('common.edit', { defaultValue: 'Edit' })}
                        disabled={vm.id.trim() === ''}
                      >
                        <Pencil className="w-4 h-4 text-sky-500 hover:text-sky-800" />
                      </button>

                      <button
                        type="button"
                        data-qa-id="delete-vm"
                        onClick={() => {
                          void onAskDelete(vm);
                        }}
                        className="cursor-pointer"
                        aria-label={t('common.delete', { defaultValue: 'Delete' })}
                        title={t('common.delete', { defaultValue: 'Delete' })}
                      >
                        <Trash2 className="w-4 h-4 text-red-600 hover:text-red-800" />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
}
